import { useRef, useEffect } from "react";
import {
  defer,
  Observable,
  map,
  combineLatest,
  distinctUntilChanged,
  switchMap,
  EMPTY,
} from "rxjs";
import type { MeetingSession } from "amazon-chime-sdk-js";

import { retryBackoffWithCaptureException } from "util/rxjs";
import type { Devices } from "common/selected_devices_controller";
import type { VideoBackgroundSettings } from "common/video_conference";
import { useBehaviorSubject } from "util/rxjs/hooks";

import { RETRY_BACKOFF_CONFIG } from ".";
import { getVideoInputDevice, isVideoBackgroundEqual } from "./background";

type LocalTracksProps = {
  selectedDevices: Devices;
  session: MeetingSession;
  muted: boolean;
  privacyVideoAspectRatio?: boolean;
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  sdkModule: typeof import("amazon-chime-sdk-js");
  videoBackground: VideoBackgroundSettings | undefined;
};
type ComposeOptions = {
  session: MeetingSession;
  sdkModule: LocalTracksProps["sdkModule"];
  props$: Observable<LocalTracksProps>;
  videoElem: HTMLVideoElement;
  audioElem: HTMLAudioElement;
};

function getAudio(options: ComposeOptions): Observable<true> {
  const { props$, session } = options;
  const { audioVideo } = session;
  return props$.pipe(
    map((props) => props.selectedDevices.microphone),
    distinctUntilChanged(),
    switchMap((microphoneId) => {
      return defer(() => audioVideo.listAudioInputDevices()).pipe(
        switchMap((allDevices) => {
          if (!allDevices.length) {
            return EMPTY;
          }
          const { deviceId } = allDevices.find((d) => d.deviceId === microphoneId) || allDevices[0];
          return new Observable<true>((observer) => {
            audioVideo
              .startAudioInput(deviceId)
              .then(() => observer.next(true))
              .catch((e) => observer.error(e));
            const muteSub = props$
              .pipe(
                map((props) => Boolean(props.muted)),
                distinctUntilChanged(),
              )
              .subscribe((muted) => {
                if (muted) {
                  audioVideo.realtimeMuteLocalAudio();
                } else {
                  audioVideo.realtimeUnmuteLocalAudio();
                }
              });
            return () => {
              muteSub.unsubscribe();
              audioVideo.stopAudioInput();
            };
          });
        }),
      );
    }),
  );
}

function getVideo(options: ComposeOptions): Observable<true> {
  const { props$, session, sdkModule } = options;
  const { audioVideo } = session;
  const privacyVideoAspectRatio$ = props$.pipe(
    map((props) => Boolean(props.privacyVideoAspectRatio)),
    distinctUntilChanged(),
  );
  return props$.pipe(
    map((props) => props.selectedDevices.webcam),
    distinctUntilChanged(),
    switchMap((webcamId) => {
      return defer(() => audioVideo.listVideoInputDevices()).pipe(
        switchMap((allDevices) => {
          if (!allDevices.length) {
            return EMPTY;
          }
          const { deviceId } = allDevices.find((d) => d.deviceId === webcamId) || allDevices[0];
          const videoBackground$ = props$.pipe(
            map((props) => props.videoBackground),
            distinctUntilChanged(isVideoBackgroundEqual),
          );
          const videoConfig$ = combineLatest([privacyVideoAspectRatio$, videoBackground$]);
          return videoConfig$.pipe(
            switchMap(([privacyVideoAspectRatio, videoBackground]) => {
              return new Observable<true>((observer) => {
                let active = true;
                getVideoInputDevice({
                  deviceId,
                  privacyVideoAspectRatio,
                  videoBackground,
                  sdkModule,
                })
                  .then((device) => {
                    return active ? audioVideo.startVideoInput(device) : Promise.resolve(null);
                  })
                  .then(() => {
                    return active
                      ? audioVideo.startVideoPreviewForVideoInput(options.videoElem)
                      : Promise.resolve();
                  })
                  .then(() => observer.next(true))
                  .catch((e) => observer.error(e));
                return () => {
                  active = false;
                  audioVideo.stopVideoInput();
                };
              });
            }),
          );
        }),
      );
    }),
  );
}

function composeAudioVideo(options: ComposeOptions) {
  const { audioVideo } = options.session;
  return combineLatest([
    defer(() => audioVideo.bindAudioElement(options.audioElem)),
    getAudio(options),
    getVideo(options),
  ]).pipe(
    (source$) =>
      new Observable((observer) => {
        let started = false;
        const sub = source$.subscribe({
          next: () => {
            if (!started) {
              started = true;
              try {
                audioVideo.start();
                audioVideo.startLocalVideoTile();
              } catch (e) {
                observer.error(e);
              }
            }
          },
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
        return () => {
          sub.unsubscribe();
          audioVideo.stop();
        };
      }),
    retryBackoffWithCaptureException(RETRY_BACKOFF_CONFIG),
  );
}

export function LocalTracks(props: LocalTracksProps) {
  const { session, sdkModule } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const props$ = useBehaviorSubject(props);
  useEffect(() => {
    props$.next(props);
  });
  useEffect(() => {
    const sub = composeAudioVideo({
      session,
      props$,
      sdkModule,
      videoElem: videoRef.current!,
      audioElem: audioRef.current!,
    }).subscribe();
    return () => sub.unsubscribe();
  }, [props$, session]);
  return (
    <>
      <audio ref={audioRef} />
      <video ref={videoRef} />
    </>
  );
}
